import { Injectable } from '@angular/core';

import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';

@Injectable()
export default class LinearBetslipMessagesService {
    readonly messages: {
        BabComboLegsTitle: string;
        BabLegsTitle: string;
        BabResultTitle: string;
        BabResultSingleTitle: string;
        LegParlay: string;
        Double: string;
        Treble: string;
        Fold: string;
        BabEventsSubtitle: string;
        BabSGPsTitle: string;
        BabSGPTitle: string;
        BabStraightsTitle: string;
        BabStraightTitle: string;
        ComboTitle: string;
        PickParlay: string;
    };

    constructor(siteCore: Sitecore) {
        const messages = siteCore.betslip.linear;

        this.messages = {
            ComboTitle: messages.ParlaysHeader,
            BabComboLegsTitle: messages.BabComboLegsTitle,
            BabLegsTitle: messages.BabLegsTitle,
            BabResultTitle: messages.BabResultTitle,
            BabResultSingleTitle: messages.BabResultSingleTitle,
            LegParlay: messages.LegParlay,
            Double: messages.Double,
            Treble: messages.Treble,
            Fold: messages.Fold,
            BabEventsSubtitle: messages.BabEventsSubtitle,
            BabSGPsTitle: messages.BabSGPsTitle,
            BabSGPTitle: messages.BabSGPTitle,
            BabStraightsTitle: messages.BabStraightsTitle,
            BabStraightTitle: messages.BabStraightTitle,
            PickParlay: messages.PickParlay,
        };
    }

    getComboSectionTitle(isEpcotEnabled: boolean, pickCount: number, hasBetBuilderPicks: boolean, hasBetBuilderPicksSelected: boolean): string {
        if (isEpcotEnabled) {
            if (hasBetBuilderPicks) {
                return interpolateDoubleBracesString(this.messages.BabComboLegsTitle, { totalCount: pickCount });
            } else if (pickCount > 1) {
                return interpolateDoubleBracesString(this.messages.LegParlay, { count: pickCount });
            }

            return this.messages.PickParlay;
        }

        if (hasBetBuilderPicksSelected) {
            return this.messages.ComboTitle;
        }

        if (pickCount === 2) {
            return this.messages.Double;
        }

        if (pickCount === 3) {
            return this.messages.Treble;
        }

        return interpolateDoubleBracesString(this.messages.Fold, { count: pickCount });
    }

    getBetBuilderComboSectionTitle(pickCount: number): string {
        return interpolateDoubleBracesString(this.messages.BabLegsTitle, { babCount: pickCount });
    }

    getComboBetBuilderPicksDescription(nonBabSelectedPicks: number, babPickCount: number): string | undefined {
        if (babPickCount <= 0) {
            return undefined;
        }

        if (nonBabSelectedPicks <= 0) {
            return this.getBabCountMessage(babPickCount);
        }

        return `${this.getBabCountMessage(babPickCount)}, ${this.getNonBabCountMessage(nonBabSelectedPicks)}`;
    }

    getComboBetBuilderEventLegsDescription(eventsCount: number, legsCount: number, hasBetBuilderPicks: boolean): string | undefined {
        if (!hasBetBuilderPicks || eventsCount < 2 || legsCount < 2) {
            return undefined;
        }

        return interpolateDoubleBracesString(this.messages.BabEventsSubtitle, { betCount: eventsCount, legsCount });
    }

    private getNonBabCountMessage(count: number) {
        return this.getMessageByCount(count, this.messages.BabStraightTitle, this.messages.BabStraightsTitle, 'straightCount');
    }

    private getBabCountMessage(count: number) {
        return this.getMessageByCount(count, this.messages.BabSGPTitle, this.messages.BabSGPsTitle, 'babCount');
    }

    private getMessageByCount(count: number, singularMessage: string, pluralMessage: string, interpolateKey: string) {
        if (count === 1) {
            return singularMessage;
        }

        return interpolateDoubleBracesString(pluralMessage, { [interpolateKey]: count });
    }
}
